import useTable from '../../../components/Table';
import { VariableSizeList as List } from 'react-window';
import useWidth from '../../../hooks/useWidth';
import { useEffect, useCallback, useRef } from 'react';
import { SortDirection } from '../../../components/Table/tableTypes';
import ReferralListRow from './ReferralListRow';
import useUserReferralStats from '../../../hooks/referral/useUserReferralStats';

export type UserReferralList = {
    referralDate: number;
    userAddress: string;
    totalEarnings: string;
    totalVolume: string;
};

type ReferralListProps = {
    dataPerRow: number;
    rowMargin: number;
};

const ReferralList: React.FC<ReferralListProps> = ({ dataPerRow, rowMargin }) => {
    const windowWidth = useWidth();

    const listRef = useRef<List<any> | null>(null);

    const { userReferralStats } = useUserReferralStats();

    const { Table, activeSort } = useTable({
        data: userReferralStats?.referralList,
        initialSortKey: 'referralDate',
    });

    const sortedData = userReferralStats?.referralList
        ? userReferralStats.referralList.sort((a, b) => {
              const direction = activeSort.direction === SortDirection.ASC ? 1 : -1;

              const aValue = parseFloat(a[activeSort.sortKey].toString());
              const bValue = parseFloat(b[activeSort.sortKey].toString());

              return (aValue - bValue) * direction;
          })
        : [];

    const getHeight = useCallback(() => {
        let height;

        // responsive card heights
        switch (true) {
            case windowWidth <= 768:
                height = 150;
                break;
            case windowWidth <= 900:
                height = 91;
                break;
            default:
                height = 97;
                break;
        }

        return height + rowMargin;
    }, [windowWidth, rowMargin]);

    // resets cache of rows
    useEffect(() => {
        if (listRef.current) {
            listRef.current.resetAfterIndex(0);
        }
    }, [windowWidth]);

    return (
        <Table className='referral-list-table'>
            <div className='row header'>
                <Table.Header sortKey='referralDate' withSort>
                    Referral Date
                </Table.Header>
                <Table.Header>User</Table.Header>
                <Table.Header sortKey='totalEarnings' withSort>
                    Total Earnings
                </Table.Header>
                <Table.Header sortKey='totalVolume' withSort>
                    Volume
                </Table.Header>
            </div>
            <List
                width={'100%'}
                ref={listRef}
                height={getHeight() * Math.min(sortedData.length, dataPerRow)}
                itemCount={sortedData.length}
                itemSize={getHeight}
                className='row-container'
            >
                {({ index, style }) => {
                    return (
                        <ReferralListRow
                            key={index}
                            payout={sortedData[index]}
                            style={{
                                ...style,
                                height: (style.height as number) - rowMargin,
                            }}
                        />
                    );
                }}
            </List>
        </Table>
    );
};

export default ReferralList;
